import { ChangelogEntry } from "./types";

export const changelogData: ChangelogEntry[] = [
  {
    version: "4.X.X",
    date: "2026-03-02",
    type: "minor",
    title: "Watchlist API Enhancements",
    description:
      "Enhanced watchlist endpoints with improved request tracking and updated response structures for better data management.",
    changes: [
      {
        type: "changed",
        category: "GET /watchlist/suppliers Response",
        description:
          "Updated supplier response payload with threat scoring and nullable supplier ID support",
        details: [
          "Added threat_score field to supplier objects",
          "Updated supplier_id to support null values",
          "Aligned response examples with latest API payload",
        ],
        endpoints: [
          {
            method: "GET",
            path: "/watchlist/suppliers",
            description: "Get watchlist suppliers with updated response fields",
          },
          {
            method: "GET",
            path: "/watchlist/suppliers/{supplierId}",
            description:
              "Get specific watchlist supplier with updated response fields",
          },
        ],
      },
      {
        type: "changed",
        category: "GET /watchlist Response",
        description: "Updated GET /watchlist response structure",
        details: [
          "Response now returns data as an array of watchlist items",
          "Added pagination support with page, total_pages, size, total_records, next, previous, first, last",
          "Added progress object tracking vulnerability, threats, and breach scan completion percentages",
          "Removed api_key_id field from response",
          "Updated example values to reflect actual API responses",
        ],
        endpoints: [
          {
            method: "GET",
            path: "/watchlist",
            description: "Get watchlist configuration with new response format",
          },
        ],
      },
      {
        type: "added",
        category: "Query Parameters",
        description:
          "Added request tracking query parameters to watchlist endpoints",
        details: [
          "requestedByClient (string, optional) - User email making the request",
          "requestedByApplication (string, optional) - Unique app name making the request",
          "cyberSecurityRiskCategory (string, optional) - Watchlist name for categorization",
        ],
        endpoints: [
          {
            method: "GET",
            path: "/watchlist",
            description: "Get watchlist configuration",
          },
          {
            method: "PATCH",
            path: "/watchlist",
            description: "Update watchlist configuration",
          },
          {
            method: "GET",
            path: "/watchlist/suppliers",
            description: "Get watchlist suppliers",
          },
          {
            method: "POST",
            path: "/watchlist/suppliers",
            description: "Add suppliers to watchlist",
          },
          {
            method: "GET",
            path: "/watchlist/report",
            description: "Get supplier report",
          },
          {
            method: "GET",
            path: "/watchlist/threats",
            description: "Get supplier threats",
          },
          {
            method: "GET",
            path: "/watchlist/compromised-accounts",
            description: "Get supplier breaches",
          },
          {
            method: "POST",
            path: "/watchlist/scan",
            description: "Scan watchlist",
          },
        ],
      },
    ],
  },
  {
    version: "4.20.0",
    date: "2025-11-03",
    type: "minor",
    title: "Enhanced Email Breach Checking",
    description:
      "Added the ability to check specific email addresses for breaches in addition to domain-wide searches.",
    changes: [
      {
        type: "added",
        category: "Breached and Exposed Emails",
        description: "Email-specific breach checking capability",
        details: [
          "Added optional 'email' query parameter to check specific email addresses",
          "Returns detailed breach information including leak details, masked passwords, and timestamps",
          "Supports both domain-wide and individual email breach searches",
          "Enhanced response includes breach status",
        ],
        endpoints: [
          {
            method: "GET",
            path: "/identity/email/breached",
            description:
              "Check for breached emails by domain or specific email address",
          },
        ],
      },
    ],
  },
  {
    version: "4.18.0",
    date: "2024-09-25",
    type: "minor",
    title: "Watchlist Management System",
    description:
      "Introducing comprehensive watchlist functionality for monitoring and managing suppliers and their security threats, including threat detection, breach monitoring, and security scoring.",
    changes: [
      {
        type: "added",
        category: "Watchlist Configuration",
        description: "Core watchlist configuration and management endpoints",
        details: [
          "Update watchlist configuration settings",
          "Get current watchlist configuration",
          "Comprehensive watchlist management capabilities",
        ],
        endpoints: [
          {
            method: "PATCH",
            path: "/watchlist",
            description: "Update watchlist configuration",
          },
          {
            method: "GET",
            path: "/watchlist",
            description: "Get watchlist configuration",
          },
        ],
      },
      {
        type: "added",
        category: "Supplier Management",
        description: "Supplier management within watchlists",
        details: [
          "Add suppliers to watchlist for monitoring",
          "Retrieve all suppliers in watchlist",
          "Get detailed information about specific suppliers",
          "Monitor supplier security and threat status",
        ],
        endpoints: [
          {
            method: "POST",
            path: "/watchlist/suppliers",
            description: "Add suppliers to watchlist",
          },
          {
            method: "GET",
            path: "/watchlist/suppliers",
            description: "Get watchlist suppliers",
          },
          {
            method: "GET",
            path: "/watchlist/suppliers/{supplierId}",
            description: "Get specific watchlist supplier",
          },
        ],
      },
      {
        type: "added",
        category: "Reporting & Analytics",
        description:
          "Comprehensive reporting and analytics for watchlist monitoring",
        details: [
          "Generate detailed supplier security reports",
          "Monitor supplier threats and vulnerabilities",
          "Track compromised accounts and breaches",
          "Perform security scans on watchlist items",
        ],
        endpoints: [
          {
            method: "GET",
            path: "/watchlist/report",
            description: "Get supplier report",
          },
          {
            method: "GET",
            path: "/watchlist/threats",
            description: "Get supplier threats",
          },
          {
            method: "GET",
            path: "/watchlist/threats/{threatId}",
            description: "Get specific supplier threat",
          },
          {
            method: "GET",
            path: "/watchlist/compromised-accounts",
            description: "Get supplier breaches",
          },
          {
            method: "GET",
            path: "/watchlist/compromised-accounts/{breachId}",
            description: "Get specific supplier breach",
          },
          {
            method: "POST",
            path: "/watchlist/scan",
            description: "Scan watchlist",
          },
        ],
      },
      {
        type: "added",
        category: "Score Profile",
        description: "Security scoring and profiling capabilities",
        details: ["Access comprehensive security score profiles"],
        endpoints: [
          {
            method: "GET",
            path: "/score-profile",
            description: "Get score profile",
          },
        ],
      },
    ],
  },
  {
    version: "4.0.0",
    date: "2024-01-15",
    type: "major",
    title: "Scoreboard V4 - Asynchronous Architecture",
    description:
      "Major architectural overhaul transitioning from synchronous to asynchronous scanning with enhanced performance and reliability.",
    changes: [
      {
        type: "changed",
        category: "Architecture",
        description:
          "Transition from synchronous to asynchronous scan processing",
        details: [
          "More consistent and accurate results for domain scoring",
          "Asynchronous queries with increased gateway timeouts",
          "Enhanced Cyber Risk architecture for improved performance",
          "Higher throughput of domain processing",
          "Support for additional endpoints and cyber threat endpoints",
          "General security improvements",
        ],
      },
      {
        type: "changed",
        category: "API Response Structure",
        description: "Modified response body structure for consistency",
        migration: {
          from: "V3 - Report data in root of response",
          to: "V4 - Report data nested inside 'data' key",
          steps: [
            "Update your API clients to access report data from the 'data' property",
            "Ensure error handling accounts for the new response structure",
            "Test all affected endpoints with the new structure",
          ],
          example: {
            before: `{
  "report_id": "123",
  "status": "completed",
  "score": 85,
  ...
}`,
            after: `{
  "error": false,
  "data": {
    "report_id": "123",
    "status": "completed", 
    "score": 85,
    ...
  },
  "message": "✔"
}`,
          },
        },
        endpoints: [
          { method: "POST", path: "/report" },
          { method: "GET", path: "/report/{link_id}" },
          { method: "PATCH", path: "/report/{link_id}/rescan-failures" },
        ],
      },
      {
        type: "added",
        category: "Query Parameters",
        description: "New query parameters for enhanced control",
        details: [
          "disable_wait - Control request waiting behavior (default: false)",
          "disable_cache - Control report caching (default: false, 15min cache)",
          "expire_at - Custom expiration date (default: 1 month)",
        ],
        endpoints: [
          { method: "POST", path: "/report" },
          { method: "GET", path: "/report/{link_id}" },
        ],
      },
      {
        type: "removed",
        category: "Endpoints",
        description: "Deprecated redundant endpoints",
        details: [
          "GET /report - Removed as it duplicated POST /report functionality with less information",
        ],
        endpoints: [{ method: "GET", path: "/report" }],
      },
      {
        type: "added",
        category: "Endpoints",
        description: "New endpoint for failure recovery",
        details: [
          "PATCH /report/{link_id}/rescan-failures - Rescan failed sections of a report",
        ],
        endpoints: [
          { method: "PATCH", path: "/report/{link_id}/rescan-failures" },
        ],
      },
    ],
  },
];
